import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  CalendarIcon,
  ClockIcon,
  AcademicCapIcon,
  EyeIcon,
  ChartBarIcon,
  UserGroupIcon,
  BookOpenIcon,
  PhoneIcon,
  MapPinIcon,
  InformationCircleIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import PageHeader from '../components/PageHeader';

const BatchManagement = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [batchStudents, setBatchStudents] = useState([]);
  const [batchMaterials, setBatchMaterials] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (batchId) {
      fetchBatchDetails(batchId);
    } else {
      fetchBatches();
    }
  }, [batchId]);

  // Handle both slug and ID-based URLs
  const getBatchIdentifier = (batch) => {
    return batch.slug || batch.id;
  };

  const fetchBatchDetails = async (slug) => {
    try {
      setDetailLoading(true);
      const [batch, students, materials] = await Promise.all([
        apiService.getBatchBySlug(slug),
        apiService.getBatchStudentsBySlug(slug),
        apiService.getCourseMaterialsBySlug(slug)
      ]);
      
      setSelectedBatch(batch);
      setBatchStudents(students);
      setBatchMaterials(materials);
    } catch (error) {
      console.error('Failed to fetch batch details:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const data = await apiService.getMyBatches();
      // Handle different response formats
      if (Array.isArray(data)) {
        setBatches(data);
      } else if (data && data.results && Array.isArray(data.results)) {
        setBatches(data.results);
      } else {
        console.warn('Unexpected batches data format:', data);
        setBatches([]);
      }
    } catch (error) {
      console.error('Failed to fetch batches:', error);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudents = async (batch) => {
    try {
      const students = await apiService.getBatchStudents(batch.id);
      setBatchStudents(students);
      setSelectedBatch(batch);
      setShowStudentsModal(true);
    } catch (error) {
      console.error('Failed to fetch batch students:', error);
    }
  };

  const handleViewDetails = (batch) => {
    // Use the slug from the backend or create a fallback
    const batchSlug = batch.slug || batch.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `batch-${batch.id.slice(0, 8)}`;
    navigate(`/batches/${batchSlug}`);
  };

  if (loading || detailLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
        {/* Aitech Logo Animation */}
        <div className="relative">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
            <span className="text-2xl font-bold text-white">A</span>
          </div>
          <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl blur opacity-30 animate-pulse"></div>
        </div>
        
        {/* Loading Text */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Loading Batches</h3>
          <p className="text-sm text-gray-500">Please wait while we fetch your batch information...</p>
        </div>
        
        {/* Modern Loading Animation */}
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-64 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full animate-pulse"></div>
        </div>
      </div>
    );
  }

  // Detail View
  if (batchId && selectedBatch) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/batches')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Batches
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{selectedBatch.name}</h1>
              <p className="text-lg text-gray-600">{selectedBatch.course?.name || selectedBatch.course?.title || 'Course Name'}</p>
              <p className="text-sm text-gray-500">Batch ID: {selectedBatch.slug || selectedBatch.id}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleViewStudents(selectedBatch)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
            >
              View Students
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Course Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <AcademicCapIcon className="h-6 w-6 mr-3 text-primary-600" />
                Course Information
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">Course</span>
                  <span className="text-gray-700">{selectedBatch.course?.name || selectedBatch.course?.title || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">Duration</span>
                  <span className="text-gray-700">{selectedBatch.duration_weeks || selectedBatch.duration || 0} weeks</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">Start Date</span>
                  <span className="text-gray-700">{new Date(selectedBatch.start_date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">End Date</span>
                  <span className="text-gray-700">{selectedBatch.end_date ? new Date(selectedBatch.end_date).toLocaleDateString() : 'TBD'}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">Class Type</span>
                  <span className="text-gray-700 capitalize">{selectedBatch.class_type || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="font-semibold text-gray-900">Status</span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedBatch.status === 'active' ? 'bg-green-100 text-green-800' :
                    selectedBatch.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedBatch.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Schedule Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <ClockIcon className="h-6 w-6 mr-3 text-primary-600" />
                Schedule
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">Days</span>
                  <span className="text-gray-700">{selectedBatch.schedule_days || 'Not set'}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-semibold text-gray-900">Time</span>
                  <span className="text-gray-700">{selectedBatch.schedule_time || 'Not set'}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="font-semibold text-gray-900">Schedule</span>
                  <span className="text-gray-700">{selectedBatch.schedule || 'Not set'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Students and Materials */}
          <div className="lg:col-span-2">
            {/* Students Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <UsersIcon className="h-6 w-6 mr-3 text-primary-600" />
                  Students ({batchStudents.length})
                </h2>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-600">Capacity: <span className="font-semibold text-gray-900">{selectedBatch.max_students || 'Unlimited'}</span></span>
                  <span className="text-gray-600">Available: <span className="font-semibold text-gray-900">
                    {selectedBatch.max_students ? 
                      Math.max(0, selectedBatch.max_students - batchStudents.length) : 
                      'Unlimited'
                    }
                  </span></span>
                </div>
              </div>

              {batchStudents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Student
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {batchStudents.map((student) => (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                  <span className="text-sm font-semibold text-primary-600">
                                    {student.student_detail?.full_name?.[0] || student.student_name?.[0] || 'S'}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.student_detail?.full_name || student.student_name || 'Student Name'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{student.student_detail?.email || student.email || 'No email'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{student.student_detail?.phone || student.phone || 'No phone'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              student.status === 'active' ? 'bg-green-100 text-green-800' :
                              student.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {student.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Link
                              to={`/progress/${student.id}/${selectedBatch.course?.id}`}
                              className="text-primary-600 hover:text-primary-500 mr-3"
                            >
                              <ChartBarIcon className="h-4 w-4 inline" />
                            </Link>
                            <Link
                              to={`/students/${student.id}`}
                              className="text-primary-600 hover:text-primary-500"
                            >
                              <EyeIcon className="h-4 w-4 inline" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No students in this batch</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Students will appear here once they are enrolled.
                  </p>
                </div>
              )}
            </div>

            {/* Materials Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <BookOpenIcon className="h-6 w-6 mr-3 text-primary-600" />
                Course Materials ({Array.isArray(batchMaterials) ? batchMaterials.length : 0})
              </h2>

              {Array.isArray(batchMaterials) && batchMaterials.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {batchMaterials.map((material) => (
                    <div key={material.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1">{material.title}</h3>
                          <p className="text-xs text-gray-600 mb-2">{material.description || 'No description'}</p>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Type: {material.material_type}</span>
                            {material.is_public && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Public
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No materials available</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Course materials will appear here once they are uploaded.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Students Modal */}
        {showStudentsModal && selectedBatch && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Students in {selectedBatch.name}
                  </h3>
                  <button
                    onClick={() => setShowStudentsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Student
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progress
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {batchStudents.map((student) => (
                        <tr key={student.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                  <span className="text-sm font-medium text-primary-600">
                                    {student.student_detail?.full_name?.[0] || student.student_name?.[0] || 'S'}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {student.student_detail?.full_name || student.student_name || 'Student Name'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{student.student_detail?.email || student.email || 'No email'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-primary-600 h-2 rounded-full"
                                  style={{ width: `${student.progress || 0}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-500">{student.progress || 0}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              student.status === 'active' ? 'bg-green-100 text-green-800' :
                              student.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {student.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Link
                              to={`/progress/${student.id}/${selectedBatch.course?.id}`}
                              className="text-primary-600 hover:text-primary-500 mr-3"
                            >
                              <ChartBarIcon className="h-4 w-4 inline" />
                            </Link>
                            <Link
                              to={`/students/${student.id}`}
                              className="text-primary-600 hover:text-primary-500"
                            >
                              <EyeIcon className="h-4 w-4 inline" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {batchStudents.length === 0 && (
                  <div className="text-center py-8">
                    <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No students in this batch</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Students will appear here once they are enrolled.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      <PageHeader
        title="My Batches"
        subtitle="Manage your assigned batches and view student progress"
      />

      {/* Batches Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {batches.map((batch) => (
          <div 
            key={batch.id} 
            className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 hover:shadow-xl hover:ring-2 hover:ring-gray-300 transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
            onClick={() => handleViewDetails(batch)}
          >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-emerald-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Content */}
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative p-3 rounded-2xl bg-emerald-500 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <UserGroupIcon className="h-6 w-6 text-white" />
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-gray-800 transition-colors duration-200">
                      {batch.name}
                    </h3>
                    <p className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-200">
                      {batch.course?.name || batch.course?.title || 'No Course'}
                    </p>
                  </div>
                </div>
                
                {/* Arrow Icon */}
                <div className="text-gray-300 group-hover:text-gray-400 transition-colors duration-200">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                  <UsersIcon className="h-4 w-4 mr-3 text-emerald-500" />
                  <span className="font-medium">{batch.current_student_count || batch.student_count || 0}</span>
                  <span className="ml-1">students</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                  <CalendarIcon className="h-4 w-4 mr-3 text-blue-500" />
                  <span>Started: {new Date(batch.start_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                  <ClockIcon className="h-4 w-4 mr-3 text-purple-500" />
                  <span>Duration: {batch.duration_weeks || batch.duration || 0} weeks</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                  <AcademicCapIcon className="h-4 w-4 mr-3 text-orange-500" />
                  <span className="capitalize">{batch.status}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  batch.status === 'active' ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800' :
                  batch.status === 'completed' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800' :
                  'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800'
                }`}>
                  {batch.status}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewStudents(batch);
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all duration-200"
                >
                  View Students
                </button>
              </div>
              
              {/* Bottom accent line */}
              <div className="mt-4 h-0.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {batches.length === 0 && (
        <div className="text-center py-16">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-2xl flex items-center justify-center mb-6">
            <UserGroupIcon className="h-10 w-10 text-emerald-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No batches assigned</h3>
          <p className="text-sm text-gray-500 mb-8 max-w-md mx-auto">
            You haven't been assigned to any batches yet. Contact your administrator to get assigned to batches.
          </p>
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl">
            Contact Administrator
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchManagement; 